"use client";

import AuthNotice from "@/components/auth/AuthNotice";
import SecurityLockPanel from "@/components/auth/SecurityLockPanel";
import { Button, buttonVariants } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getSmsRetryAfter, useSmsCooldown } from "@/hooks/use-sms-cooldown";
import { useAuth } from "@/lib/auth";
import { authNotice, parseAuthError, type AuthMessage } from "@/lib/auth-errors";
import { authCookieFlags, getSiteToken, glob, useApi } from "@/lib/pariette";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import { ArrowLeft, AtSign, Clock3, Eye, EyeOff, KeyRound, Loader2, LockKeyhole, MessageSquareText, RefreshCcw, ShieldCheck, Smartphone, Store, UserPlus2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Giriş formu (yeniden tasarım 5 Eki 2026). Mesajlar backend'in 8426xxx
// kodlarıyla gelir ve AuthNotice ile gösterilir (kod + ref, destek takibi).

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Alan hataları: genel FormMessage'ın kırmızı bloğu yerine satır içi kısa metin.
const fieldError = "items-center gap-1.5 bg-transparent p-0 text-xs font-medium text-destructive [&>span]:w-auto [&_svg]:size-3.5";

export default function Component() {
  const { login } = useAuth({ middleware: "guest" });
  const t = useTranslations("form");
  const tAuth = useTranslations("auth");
  const locale = useLocale();
  const [notice, setNotice] = useState<AuthMessage | null>(null);
  // Kilit kalkınca (kendi açtı ya da süre doldu) gösterilen kısa not; yeni denemede silinir.
  const [unlockedNotice, setUnlockedNotice] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const [security, setSecurity] = useState("password");
  const [isPending, setIsPending] = useState(false);
  const [captchaImage, setCaptchaImage] = useState<null | string | undefined>();
  const [captchaText, setCaptchaText] = useState<string>("");
  const [TFA, setTFA] = useState(false);
  const [pollData, setPollData] = useState<PollData>(null);
  const params = useSearchParams();
  const [isPassReset, setIsPassReset] = useState<boolean>(false);
  const { $post } = useApi(true);
  // app_2fa login için /auth/level'dan dönen nonce (gönderimde forma bağlanır).
  // Ref eşleniği: onSubmit closure'ı await sonrası state'i göremeyebiliyor.
  const [levelToken, setLevelToken] = useState<string | null>(null);
  const levelTokenRef = useRef<string | null>(null);
  const lastLevelFor = useRef<string>("");

  const formSchema = z.object({
    identifier: z
      .string()
      .trim()
      .min(1, { message: tAuth("loginIdentifierRequired") })
      .refine((val) => isEmail(val) || /^\+?[0-9]{10,}$/.test(val.replace(/\s/g, "")), { message: tAuth("loginIdentifierInvalid") }),
    // Eski hesaplarda 6 karakterden kısa şifre olabilir: yalnız boş olmasın.
    password: z.string().min(1, { message: tAuth("loginPasswordRequired") }),
    store_code: glob.site_token === glob.aysoft_token ? z.string().trim().min(1, { message: tAuth("loginStoreCodeRequired") }) : z.string(),
    confirmation: z.string(),
    user_code: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { identifier: "", password: "", store_code: "", confirmation: "", user_code: "" },
  });

  // /auth/level: her tuşta değil, alandan çıkınca ve gönderirken (5 Eki 2026).
  const getSecurity = async (identifier: string | undefined, force = false) => {
    const id = (identifier ?? "").trim();
    if (id.length < 6) return;
    if (!force && id === lastLevelFor.current) return security;
    lastLevelFor.current = id;
    const data: Record<string, string> = isEmail(id) ? { email: id } : { phone: id };

    return await $post(`auth/level`, data)
      .then((res) => {
        setSecurity(res.data);
        setCaptchaImage(res?.extention?.captcha);
        setCaptchaText("");
        const tok = res?.extention?.login_method === "app_2fa" ? res?.extention?.level_token ?? null : null;
        setLevelToken(tok);
        levelTokenRef.current = tok;
        return res.data as string;
      })
      .catch(() => {
        setLevelToken(null);
        levelTokenRef.current = null;
        return undefined;
      });
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    // Kilit paneli açıkken giriş gönderilmez (Enter dahil); API de 423 döner.
    if (notice?.lock) return false;
    // Çift gönderim kilidi: buton auth/level beklenirken de kapalı kalmalı.
    // İkinci istek ilk push'un 10 sn sınırına takılıp 429 veriyor, ilk
    // isteğin onay ekranı da o mesajı siliyordu (5 Eki 2026).
    if (submittingRef.current) return false;
    // Onay beklenirken Enter yeni giriş başlatmaz (kod adımı hariç).
    if (isPending && !TFA) return false;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await submitLogin(values);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  async function submitLogin(values: z.infer<typeof formSchema>) {
    setUnlockedNotice(false);
    setNotice(null);
    const checkSecurityLast = await getSecurity(values.identifier, true);
    if (checkSecurityLast == "need_code" && values.user_code.length < 6) {
      form.setError("user_code", { message: tAuth("loginUserCodeRequired") });
      return false;
    }
    if (captchaImage && captchaText === "") {
      setNotice(authNotice("warning", tAuth("loginCaptchaRequiredTitle"), tAuth("loginCaptchaRequiredMessage")));
      return false;
    }
    setIsPassReset(false);
    const payload: any = { password: values.password };
    if (isEmail(values.identifier)) payload.email = values.identifier.trim();
    else payload.phone = values.identifier.trim();
    if (checkSecurityLast == "need_code") payload.user_code = values.user_code;
    if (TFA) payload.confirmation = values.confirmation;
    if (captchaImage) payload.captcha = captchaText;
    payload.store_code = values.store_code;
    payload.locale = locale;
    payload.redirect = false;
    if (levelTokenRef.current) payload.level_token = levelTokenRef.current;
    await login({ form: payload, setLoader, setErrors: setNotice, setTFA, setPollData, getSecurity: (id: string) => getSecurity(id, true), setIsPending });
  }

  const resetSite = () => {
    Cookies.remove("Authorization");
    localStorage.removeItem("storeData");
    window.location.reload();
  };

  useEffect(() => {
    if (params.get("status") === "passwordResetted") setIsPassReset(true);
  }, [params]);

  const [storeCode, setStoreCode] = useState<string>("");
  useEffect(() => {
    // eSIM promosyonu her yeni login'de yeniden çıksın (bkz. EsimPromoDialog.tsx).
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("esim-promo-seen")) localStorage.removeItem(key);
    });
    const storeData = JSON.parse(localStorage.getItem("storeData") ?? "{}");
    if (storeData && typeof storeData === "object" && storeData.code) {
      form.setValue("store_code", storeData.code ?? "");
      setStoreCode(storeData.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rememberedStore = getSiteToken() !== glob.aysoft_token && !!storeCode;
  const locked = !!notice?.lock;
  const inputClass = "h-11 pl-10";

  return (
    <div className="w-full space-y-5">
      {isPassReset && <AuthNotice notice={authNotice("success", t("passwordResettedLogin"))} />}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-4 text-card-foreground" method="post" translate="no" noValidate>
          {!isPending && (
            <>
              <FormField
                control={form.control}
                name="identifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{tAuth("loginIdentifierLabel")}</FormLabel>
                    <div className="relative">
                      <AtSign className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                      <FormControl>
                        <Input
                          {...field}
                          onBlur={() => {
                            field.onBlur();
                            getSecurity(field.value);
                          }}
                          readOnly={TFA}
                          className={inputClass}
                          placeholder={tAuth("loginIdentifierPlaceholder")}
                          autoComplete="username"
                          inputMode="email"
                          autoFocus
                        />
                      </FormControl>
                    </div>
                    <FormMessage className={fieldError} />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel>{t("password")}</FormLabel>
                      <Link href="/Auth/Forgot-password" className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline" tabIndex={-1}>
                        {t("forgotPass")}
                      </Link>
                    </div>
                    <div className="relative">
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                      <FormControl>
                        <Input
                          type={showPassword ? "text" : "password"}
                          {...field}
                          className={cn(inputClass, "pr-11")}
                          readOnly={TFA}
                          placeholder={tAuth("loginPasswordPlaceholder")}
                          autoComplete="current-password"
                        />
                      </FormControl>
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-1.5 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        aria-label={showPassword ? tAuth("loginHidePassword") : tAuth("loginShowPassword")}
                      >
                        {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </button>
                    </div>
                    <FormMessage className={fieldError} />
                  </FormItem>
                )}
              />

              {rememberedStore ? (
                <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/40 px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Store className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <div className="min-w-0 leading-tight">
                      <p className="text-xs text-muted-foreground">{t("store_code")}</p>
                      <p className="truncate font-mono text-sm font-medium">{storeCode}</p>
                    </div>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={resetSite} className="shrink-0 text-muted-foreground hover:text-foreground">
                    {tAuth("change_account")}
                  </Button>
                </div>
              ) : (
                <FormField
                  control={form.control}
                  name="store_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("store_code")}</FormLabel>
                      <div className="relative">
                        <Store className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <FormControl>
                          <Input {...field} readOnly={TFA} placeholder={tAuth("loginStoreCodePlaceholder")} className={cn(inputClass, "font-mono uppercase placeholder:font-sans placeholder:normal-case")} autoComplete="organization" />
                        </FormControl>
                      </div>
                      <FormMessage className={fieldError} />
                    </FormItem>
                  )}
                />
              )}

              {security === "need_code" && (
                <FormField
                  control={form.control}
                  name="user_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("user_code")}</FormLabel>
                      <FormControl>
                        <InputOTP maxLength={6} {...field} inputMode="numeric" containerClassName="w-full">
                          <InputOTPGroup className="w-full">
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                              <InputOTPSlot key={i} index={i} className="h-11 w-auto flex-1 text-base" />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </FormControl>
                      <p className="text-xs text-muted-foreground">{tAuth("loginUserCodeHint")}</p>
                      <FormMessage className={fieldError} />
                    </FormItem>
                  )}
                />
              )}

              {captchaImage && (
                <div className="space-y-2">
                  <Label htmlFor="login-captcha">{t("captcha")}</Label>
                  <div className="flex items-center gap-2 rounded-md border p-2">
                    <Image src={"data:image/png;base64," + captchaImage} alt={tAuth("loginCaptchaAlt")} className="h-12 w-auto rounded-sm bg-white" width={160} height={48} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => getSecurity(form.getValues().identifier, true)} aria-label={tAuth("loginCaptchaRefresh")}>
                      <RefreshCcw className="size-4" />
                    </Button>
                    <Input
                      id="login-captcha"
                      value={captchaText}
                      onChange={(e) => setCaptchaText(e.target.value.replace(/\D/g, "").slice(0, 3))}
                      inputMode="numeric"
                      autoComplete="off"
                      className="h-11 w-24 text-center font-mono text-base tracking-[0.3em]"
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {!isPending && locked && (
            <SecurityLockPanel
              lock={notice!.lock}
              onReleased={() => {
                setNotice(null);
                setUnlockedNotice(true);
                form.setValue("password", "");
                setTimeout(() => form.setFocus("password"), 0);
              }}
            />
          )}

          {!isPending && !notice && unlockedNotice && <AuthNotice notice={authNotice("success", tAuth("securityUnlockedEnterPassword"))} />}

          {!isPending && notice && !locked && <AuthNotice notice={notice} />}

          {!isPending && !locked && (
            <Button className="h-11 w-full font-semibold" size="lg" type="submit" disabled={loader || submitting}>
              {loader || submitting ? <Loader2 className="size-4 animate-spin" /> : <LockKeyhole className="size-4" />}
              {loader || submitting ? tAuth("loginSubmitting") : t("login")}
            </Button>
          )}

          <PendingLogin
            identifier={form.getValues("identifier")}
            password={form.getValues("password")}
            notice={notice}
            setNotice={setNotice}
            setIsPending={setIsPending}
            isPending={isPending}
            storeCode={form.getValues("store_code")}
            clearForm={() => {
              form.setValue("identifier", "");
              form.setValue("password", "");
              form.setValue("confirmation", "");
            }}
            pollData={pollData}
            setPollData={setPollData}
            setTFA={setTFA}
            TFA={TFA}
            loader={loader}
          >
            <FormField
              control={form.control}
              name="confirmation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tAuth("loginCodeLabel")}</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        className="h-11 flex-1 text-center font-mono text-lg tracking-[0.4em]"
                      />
                    </FormControl>
                    <Button className="h-11 shrink-0 px-5" type="submit" disabled={loader || field.value.length === 0}>
                      {loader ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
                      {tAuth("loginCodeSubmit")}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">{t("smsCode")}</p>
                </FormItem>
              )}
            />
          </PendingLogin>
        </form>
      </Form>

      {!isPending && (
        <Link href="/Register" className={cn(buttonVariants({ variant: "outline" }), "h-10 w-full")}>
          <UserPlus2 className="size-4" />
          {tAuth("no_account_msg")}
        </Link>
      )}
    </div>
  );
}

type Device = {
  id: number;
  device_name: string;
};

type PollData = {
  poll_token?: string;
  approval?: string;
  approver?: string;
} | null;

const APPROVAL_SECONDS = 180;

const PendingLogin = ({
  identifier,
  password,
  notice,
  setNotice,
  setIsPending,
  storeCode,
  clearForm,
  pollData,
  setPollData,
  setTFA,
  isPending,
  children,
  TFA,
  loader,
}: {
  identifier: string | undefined;
  password: string;
  notice: AuthMessage | null;
  setNotice: React.Dispatch<React.SetStateAction<AuthMessage | null>>;
  setIsPending: React.Dispatch<React.SetStateAction<boolean>>;
  storeCode: string;
  clearForm: () => void;
  pollData: PollData;
  setPollData: React.Dispatch<React.SetStateAction<PollData>>;
  setTFA: React.Dispatch<React.SetStateAction<boolean>>;
  isPending: boolean;
  children: React.ReactNode;
  TFA: boolean;
  loader: boolean;
}) => {
  const tAuth = useTranslations("auth");
  const t = useTranslations("form");
  const [showAlternatives, setShowAlternatives] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(APPROVAL_SECONDS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const problemTimerRef = useRef<NodeJS.Timeout | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const [isDeviceSelected, setDeviceSelected] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<string>("");
  const [devices, setDevices] = useState<Device[]>([]);
  const router = useRouter();
  const { $post, $get } = useApi(true);
  const params = useSearchParams();
  // Aynı numaraya 3 dakikada 1 SMS kuralı (backend 429 + retry_after döner).
  const smsCooldown = useSmsCooldown();
  const pollTokenRef = useRef<string | null>(null);
  // Cihaz değiştirme isteği sürerken: ikinci istek gitmesin, eski token'ın 404'ü yok sayılsın.
  const selectingRef = useRef(false);

  const identifierPayload = (): Record<string, string> => {
    const id = (identifier ?? "").trim();
    return isEmail(id) ? { email: id } : { phone: id };
  };

  const backToLogin = (message?: AuthMessage | null) => {
    pollTokenRef.current = null;
    setIsPending(false);
    setTFA(false);
    setPollData(null);
    setShowAlternatives(false);
    const newParams = new URLSearchParams(params);
    newParams.delete("app2fa");
    router.push(`?${newParams.toString()}`);
    clearForm();
    setNotice(message ?? null);
  };

  const handleClickSendSms = async (pw: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await $post(`auth/resend-confirmation`, { ...identifierPayload(), password: pw })
      .then(() => {
        setTFA(true);
        setPollData(null);
        setShowAlternatives(false);
        setNotice(authNotice("info", tAuth("loginCodeSentTitle"), tAuth("loginCodeSentMessage")));
      })
      .catch((error) => {
        const parsed = parseAuthError(error, tAuth);
        const retryAfter = getSmsRetryAfter(error) ?? parsed.retryAfter;
        if (retryAfter) smsCooldown.start(retryAfter);
        setNotice(parsed);
      });
  };

  // auth/user-phones güvenlik gereği device_token döndürmüyor: seçim cihaz id'siyle.
  const handleSelectDevice = async (deviceId: string) => {
    if (selectingRef.current) return;
    selectingRef.current = true;
    setSelectedDevice(deviceId);
    setDeviceSelected(true);
    setNotice(null);
    await $post(`auth/resend-login-push`, { ...identifierPayload(), password, device_id: Number(deviceId) })
      .then((res) => {
        if (res.poll_token) {
          setShowAlternatives(false);
          pollTokenRef.current = res.poll_token;
          setPollData({ poll_token: res.poll_token, approval: "pending" });
        }
      })
      .catch((e: any) => {
        const parsed = parseAuthError(e, tAuth);
        // Push gidemediyse backend SMS koduna geçer (416): kod alanını göster.
        if (parsed.needsCode) {
          setPollData(null);
          setTFA(true);
        } else {
          // 429 vb.: seçim kilitli kalmasın, kullanıcı tekrar deneyebilsin.
          setDeviceSelected(false);
          setSelectedDevice("");
        }
        setNotice(parsed);
      })
      .finally(() => {
        selectingRef.current = false;
        setCountdown(APPROVAL_SECONDS);
      });
  };

  const getDevices = async () => {
    await $post(`auth/user-phones`, { ...identifierPayload(), password })
      .then((res) => setDevices(res.data ?? []))
      .catch(() => setDevices([]));
  };

  // ── Polling-based login approval ──────────────────────────
  // poll_token her istekte rotate edilir; bir önceki token Redis'te 15 sn tutulur.
  useEffect(() => {
    if (pollData !== null && pollData.poll_token) {
      pollTokenRef.current = pollData.poll_token;
      getDevices();
      setIsPending(true);
      setNotice(null);
      const newParams = new URLSearchParams(params);
      newParams.set("app2fa", "true");
      router.push(`?${newParams.toString()}`);
      setDeviceSelected(false);
      setCountdown(APPROVAL_SECONDS);
      if (intervalIdRef.current) clearInterval(intervalIdRef.current);

      intervalIdRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 0) {
            if (intervalIdRef.current) clearInterval(intervalIdRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // 20 sn sonra alternatifleri öner
      problemTimerRef.current = setTimeout(() => setShowAlternatives(true), 20000);

      // Süre doldu
      timerRef.current = setTimeout(() => {
        backToLogin(authNotice("warning", tAuth("loginApprovalTimeoutTitle"), t("reLoginTime")));
      }, APPROVAL_SECONDS * 1000);

      let polling = false;
      const pollInterval = setInterval(async () => {
        const token = pollTokenRef.current;
        // Önceki yoklama dönmeden yenisi gitmesin (token her yanıtta değişiyor).
        if (!token || polling) return;
        polling = true;
        try {
          const res = await $get(`auth/login-status/${token}`);
          if (res.access_token) {
            pollTokenRef.current = null;
            clearInterval(pollInterval);
            Cookies.set(glob.authData.authorizationKey, res.access_token, {
              expires: glob.authData.authorizationExpires,
              ...authCookieFlags(),
            });
            if (res.stores?.length > 0) {
              localStorage.setItem("storeData", JSON.stringify({ ...res.stores[0], token: res.stores[0].store }));
            }
            setNotice(null);
            setIsPending(false);
            setPollData(null);
            const homePage = localStorage.getItem("homePage") ? `/${localStorage.getItem("homePage")}` : "";
            router.push(storeCode ? homePage || glob.authData.loginRedirect : "/Auth/Redirect");
            return;
          }
          if (res.approval === "pending" && res.poll_token) pollTokenRef.current = res.poll_token;
        } catch (err: any) {
          const code = err?.code ?? err?.status;
          // Bu arada başka cihaza geçildiyse eski token'ın hatası önemsiz.
          if (token !== pollTokenRef.current || selectingRef.current) return;
          // 403 reddedildi, 410 süresi doldu, 404 istek geçersiz kaldı (yerine
          // yenisi açıldı): kodlu mesajla girişe dön. 404 önce sessizce
          // bırakılıyordu; ekran "onay bekleniyor"da donuyor, telefondan
          // verilen onay da hiçbir şey yapmıyordu (5 Eki 2026).
          if (code === 403 || code === 410 || code === 404) {
            clearInterval(pollInterval);
            backToLogin(parseAuthError(err, tAuth));
          }
        } finally {
          polling = false;
        }
      }, 2000);

      return () => {
        pollTokenRef.current = null;
        clearInterval(pollInterval);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (problemTimerRef.current) clearTimeout(problemTimerRef.current);
        if (intervalIdRef.current) clearInterval(intervalIdRef.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollData]);

  if (!(isPending || TFA)) return null;

  const waitingPush = !!pollData && !TFA;
  const ownerApproval = pollData?.approver === "owner";

  return (
    <div className="space-y-4">
      {waitingPush && (
        <div className="space-y-4 rounded-md border p-4">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Smartphone className="size-5 text-primary" aria-hidden />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold">{ownerApproval ? tAuth("loginOwnerApprovalTitle") : tAuth("loginApprovalTitle")}</p>
              <p className="text-sm text-muted-foreground">{ownerApproval ? tAuth("waitingOwnerApproval") : tAuth("loginApprovalMessage")}</p>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Clock3 className="size-3.5" aria-hidden />
                {t("remainingTime")}
              </span>
              <span className="font-mono tabular-nums text-foreground">
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
              </span>
            </div>
            <Progress value={(countdown / APPROVAL_SECONDS) * 100} className="h-1.5" />
          </div>

          {!ownerApproval && (
            <div className="border-t pt-3">
              {!showAlternatives ? (
                <button type="button" onClick={() => setShowAlternatives(true)} className="text-sm font-medium text-muted-foreground hover:text-foreground hover:underline">
                  {tAuth("loginApprovalProblem")}
                </button>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{tAuth("loginApprovalAlternatives")}</p>
                  <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <MessageSquareText className="size-4 text-muted-foreground" aria-hidden />
                      {t("sendSms")}
                    </div>
                    <SendSmsDialog disabled={TFA || smsCooldown.active} cooldownSeconds={smsCooldown.seconds} onSendSms={handleClickSendSms} />
                  </div>
                  {devices.length > 0 && (
                    <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                      <div className="flex items-center gap-2 text-sm">
                        <Smartphone className="size-4 text-muted-foreground" aria-hidden />
                        {t("selectDevice")}
                      </div>
                      <Select value={selectedDevice} onValueChange={handleSelectDevice} disabled={isDeviceSelected}>
                        <SelectTrigger className="h-9 w-40">
                          <SelectValue placeholder={t("device")} />
                        </SelectTrigger>
                        <SelectContent>
                          {devices.map((device: Device, index) => (
                            <SelectItem key={`device-${device.id}-${index}`} value={String(device.id)}>
                              {device.device_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {TFA && <div className="rounded-md border p-4">{children}</div>}

      {notice && !notice.lock && <AuthNotice notice={notice} />}

      <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => backToLogin(null)} disabled={loader}>
        <ArrowLeft className="size-4" />
        {tAuth("loginBack")}
      </Button>
    </div>
  );
};

const SendSmsDialog = ({ disabled, cooldownSeconds = 0, onSendSms }: { disabled: boolean; cooldownSeconds?: number; onSendSms: (password: string) => void }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const t = useTranslations("form");
  const tAuth = useTranslations("auth");
  const cooling = cooldownSeconds > 0;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-9 min-w-24" variant="outline" size="sm" type="button" disabled={disabled}>
          {cooling ? <Clock3 className="size-4" /> : <MessageSquareText className="size-4" />}
          {cooling ? `${cooldownSeconds}s` : t("sms")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{tAuth("loginSmsDialogTitle")}</DialogTitle>
          <DialogDescription>{tAuth("loginSmsDialogMessage")}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!password) return;
            onSendSms(password);
            setOpen(false);
            setPassword("");
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="sms-password">{t("password")}</Label>
            <Input id="sms-password" className="h-11" value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="current-password" disabled={disabled} autoFocus />
          </div>
          <DialogFooter>
            <Button className="h-10 w-full sm:w-auto" type="submit" disabled={disabled || !password}>
              <MessageSquareText className="size-4" />
              {tAuth("loginSmsDialogSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
